import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getRememberedCategoryPath } from '../navigation/adminPlaceMemory';
import { getAdminUser } from '../navigation/sectionAccess';

import { cn } from '@/lib/utils';
import adminApi from '@/services/adminApi';

const AdminCockpitNavItem = ({
  categoryId,
  category,
  canAccessSection,
  isActive,
  onClick,
  compact = false,
  collapsed = false,
}) => {
  const Icon = category.icon;
  const [badgeCount, setBadgeCount] = useState(0);

  useEffect(() => {
    if (categoryId !== 'outreach') return undefined;

    const fetchCount = async () => {
      try {
        const response = await adminApi.get('/api/admin/inbox/unread-count');
        if (response.ok) {
          const data = await response.json();
          setBadgeCount(data.count || 0);
        }
      } catch {
        // The unread badge is helpful context, but must never block navigation.
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [categoryId]);

  return (
    <Link
      to={getRememberedCategoryPath(
        categoryId,
        getAdminUser(),
        canAccessSection,
      )}
      onClick={onClick}
      aria-label={category.label}
      data-cockpit-target={categoryId}
      className={cn(
        'relative overflow-hidden transition-colors duration-200 motion-reduce:transition-none',
        'hover:bg-stone-200/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 dark:hover:bg-background/10',
        compact
          ? 'flex min-h-[58px] flex-col items-center justify-center gap-1 px-1 py-2 text-[9px]'
          : 'grid h-[52px] min-h-[52px] grid-cols-[52px_minmax(0,1fr)] items-center rounded-md px-[18px] text-sm',
        isActive
          ? 'admin-sidebar-item-active font-medium text-amber-700 dark:text-amber-100'
          : 'text-stone-600 hover:text-stone-900 dark:text-stone-300 dark:hover:text-white'
      )}
      aria-current={isActive ? 'page' : undefined}
      title={`${category.label} cockpit`}
    >
      <span className={cn(
        'relative flex shrink-0 items-center justify-center',
        compact ? 'h-4 w-4' : 'h-[44px] w-[52px]',
      )}>
        <Icon
          className={cn(
            'h-4 w-4 flex-shrink-0',
            isActive ? 'text-amber-600 dark:text-amber-400' : 'text-amber-700 dark:text-amber-500'
          )}
          strokeWidth={1.75}
          aria-hidden="true"
        />
        {categoryId === 'outreach' && badgeCount > 0 && (
          <span className={cn(
            'absolute min-w-[20px] rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[9px] font-medium text-white',
            compact ? '-right-5 -top-2' : 'right-0 top-0',
          )}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </span>
      <span
        data-navigation-label
        className={cn(
          'min-w-0 font-semibold tracking-wide',
          compact ? 'max-w-full truncate' : 'whitespace-nowrap pl-3',
          collapsed && 'invisible',
        )}
        aria-hidden={collapsed ? 'true' : undefined}
      >
        {compact ? category.shortLabel : category.label}
      </span>
    </Link>
  );
};

export default AdminCockpitNavItem;
